import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import Breadcrumbs from '../common/Breadcrumbs';
import ThemeToggle from '../common/ThemeToggle';
import useAuth from '../../hooks/useAuth';
import alertService from '../../utils/alertService';
import { setGlobalNavigator } from '../../utils/navigation';

export const AppShell = () => {
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar-collapsed');
    return saved ? JSON.parse(saved) : false;
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const { user, logout, menus } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const profileDropdownRef = useRef(null);

  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', JSON.stringify(collapsed));
  }, [collapsed]);

  useEffect(() => {
    setGlobalNavigator(navigate);
  }, [navigate]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);


  const handleLogout = async () => {
    const confirmLogout = await alertService.confirm({
      title: 'Sign Out',
      text: 'Are you sure you want to end your session?',
      icon: 'question',
      confirmButtonText: 'Yes, Sign Out'
    });
    if (confirmLogout) {
      logout();
    }
  };

  const handleToggleSidebar = () => {
    if (window.innerWidth < 768) {
      setMobileOpen(!mobileOpen);
    } else {
      setCollapsed(!collapsed);
    }
  };

  return (
    <div className="min-h-screen flex bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 theme-transition">
      <Sidebar
        menus={menus}
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />

      <div
        className={`flex-1 flex flex-col min-w-0 min-h-screen transition-all duration-300 ${
          collapsed ? 'md:pl-20' : 'md:pl-[272px]'
        } pl-0`}
      >
        <header className="h-16 border-b border-slate-150 dark:border-slate-800/80 bg-white dark:bg-slate-900/60 backdrop-blur-md flex items-center justify-between px-6 shrink-0 sticky top-0 z-20 theme-transition">
          <div className="flex items-center gap-4">
            <button
              onClick={handleToggleSidebar}
              className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 dark:text-slate-400 focus:outline-none"
            >
              <span className="material-icons text-xl leading-none">menu</span>
            </button>
            <div className="hidden md:block">
              <Breadcrumbs />
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* <ThemeToggle /> */}

            <div ref={profileDropdownRef} className="relative">
              <button
                type="button"
                onClick={() => setProfileOpen(!profileOpen)}
                className="flex items-center gap-2.5 px-2 py-1 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800/80 focus:outline-none select-none transition-colors duration-200"
              >
                <div className="h-8 w-8 rounded-full bg-gradient-to-tr from-primary-500 to-primary-600 text-white font-bold text-sm flex items-center justify-center shadow-sm shrink-0">
                  {user?.name ? user.name.charAt(0).toUpperCase() : 'A'}
                </div>
                <div className="hidden md:flex flex-col text-left leading-tight pr-1">
                  <span className="text-xs font-bold text-slate-800 dark:text-slate-100">
                    {user?.name || 'Administrator'}
                  </span>
                  <span className="text-[11px] font-normal text-slate-400 dark:text-slate-400 capitalize">
                    {user?.role || 'User'}
                  </span>
                </div>
              </button>

              {profileOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-slate-800 border border-slate-200/80 dark:border-slate-700/80 rounded-[14px] shadow-[0_10px_25px_-5px_rgba(0,0,0,0.08)] dark:shadow-[0_10px_25px_-5px_rgba(0,0,0,0.4)] animate-zoom-in overflow-hidden transition-all duration-200 z-50">
                  <div className="px-4 py-2.5 border-b border-slate-100 dark:border-slate-700/60">
                    <p className="text-xs font-bold text-slate-800 dark:text-slate-100 truncate leading-snug">
                      {user?.name || 'Administrator'}
                    </p>
                    <p className="text-[11px] font-normal text-slate-400 dark:text-slate-400 truncate mt-0.5">
                      {user?.email || 'user@ratnakar.com'}
                    </p>
                  </div>

                  <div className="p-1">
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="flex w-full items-center gap-2.5 px-3 py-2 text-xs font-semibold text-[#DC2626] rounded-[8px] hover:bg-[#FEF2F2] dark:hover:bg-red-950/40 transition-colors duration-200"
                    >
                      <span className="material-icons text-base leading-none text-[#DC2626]">logout</span>
                      <span className="leading-none">Sign Out</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="flex-1 p-6 overflow-y-auto animate-fade-in bg-slate-50/50 dark:bg-slate-950">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AppShell;
